import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../Utils/useSEO";
import { motion } from "framer-motion";
import { ScrollReveal, ScrollRevealItem } from "../components/ScrollReveal";
import { Users, Sparkles, ShieldCheck, Cpu, Globe, } from "lucide-react";

export default function AboutPage() {
  useSEO({
    title: "About Us // Empowering Autonomous Logistics Operations",
    description: "Meet the team of systems architects, AI engineers, and logistics specialists automating the core enterprise stack at Supply Spot."
  });

  const [activeTab, setActiveTab] = useState("story");

  const stats = [
    { value: "500+", label: "Suppliers Connected" },
    { value: "$10B+", label: "Transaction Volume" },
    { value: "99.9%", label: "Platform Uptime" },
    { value: "12 Hrs", label: "Avg Support SLA" },
  ];

  const coreValues = [
    {
      icon: <Cpu className="w-5 h-5 text-brand-orange" />,
      title: "Intelligent Autonomy",
      desc: "We build systems that reduce friction and eliminate manual labor, allowing supply chains to self-correct in real-time."
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-brand-amber" />,
      title: "Bulletproof Compliance",
      desc: "Our verification nodes ensure complete corporate integrity, ESG standards compliance, and financial accuracy."
    },
    {
      icon: <Globe className="w-5 h-5 text-brand-gold" />,
      title: "Global Resilience",
      desc: "From local warehouses to global deep sea channels, we keep cargo moving dynamically through volatile conditions."
    }
  ];

  const milestones = [
    {
      year: "2023",
      title: "Founding & Conception",
      desc: "Formed by logistics and database veterans in Bangalore & Coimbatore, targeting manual compliance bottlenecks."
    },
    {
      year: "2024",
      title: "Series A & S/4HANA Sync",
      desc: "Secured $12M Series A funding and developed native bi-directional ERP integration adaptors."
    },
    {
      year: "2025",
      title: "Aether Core AI Launch",
      desc: "Released our flagship cognitive AI engine, automating 3-way matching and risk prediction models."
    },
    {
      year: "2026",
      title: "Global Scaling Nodes",
      desc: "Established physical offices in Singapore & Dubai, passing $10B+ in annual transaction volume."
    }
  ];

  const team = [
    {
      name: "Sanjay Kulkarni",
      role: "CEO & Co-founder",
      bio: "Former Head of Supply Chain Architecture at Global Tech Corp. Led core migrations for over 50 enterprise logistics grids.",
      initials: "SK",
      color: "from-brand-orange to-brand-amber"
    },
    {
      name: "Priya Sharma",
      role: "CTO & Co-founder",
      bio: "S/4HANA system veteran and researcher in distributed database systems. Ex-Oracle Cloud Lead Database Architect.",
      initials: "PS",
      color: "from-brand-amber to-brand-gold"
    },
    {
      name: "David Chen",
      role: "Head of Product & AI",
      bio: "Pioneered automated category-spend threat modeling models. Ex-DeepMind Systems Engineer.",
      initials: "DC",
      color: "from-brand-gold to-brand-orange"
    },
    {
      name: "Marcus Vance",
      role: "VP of Logistics Systems",
      bio: "Logistics specialist with 15+ years experience. Expert in JIT scheduling structures and transit tracking setups.",
      initials: "MV",
      color: "from-brand-orange to-brand-gold"
    }
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary">
      <Navbar />

      {/* Grid Overlay */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Glow Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-brand-amber/10 rounded-full blur-[140px] -z-10 pointer-events-none" />

      {/* HERO SECTION */}
      <section className="relative pt-36 pb-20 px-6 max-w-7xl mx-auto text-center flex flex-col items-center gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-semibold uppercase tracking-wider shadow-md"
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>ABOUT SUPPLY SPOT</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-text-primary leading-none font-title max-w-4xl"
        >
          Powering the Future of <br />
          <span className="bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold bg-clip-text text-transparent drop-shadow-md">
            Autonomous Operations
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-text-secondary text-sm md:text-base max-w-2xl leading-relaxed mt-2"
        >
          We are a team of systems architects, AI engineers, and logistics specialists dedicated to eliminating operational latency, manual auditing overhead, and supplier coordination gaps.
        </motion.p>
      </section>

      {/* STATS SECTION */}
      <section className="py-12 border-y border-slate-900 bg-bg-secondary/10 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <ScrollReveal staggerChildren={0.06} className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((stat, idx) => (
              <ScrollRevealItem key={idx}>
                <div className="glass-panel p-6 rounded-xl border border-brand-orange/10">
                  <div className="text-3xl sm:text-4xl font-title font-extrabold text-brand-amber number-glow">
                    {stat.value}
                  </div>
                  <div className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mt-2">
                    {stat.label}
                  </div>
                </div>
              </ScrollRevealItem>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* MISSION, STORY & MILIESTONES SECTION */}
      <section className="px-6 py-24 max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Switchable Tabs & Mission Statement */}
          <ScrollReveal className="lg:col-span-5 flex flex-col gap-6 text-left">
            <div className="flex gap-4 border-b border-border-theme pb-3">
              <button
                onClick={() => setActiveTab("story")}
                className={`font-title font-bold text-xs tracking-widest uppercase transition-all ${activeTab === "story" ? "text-brand-orange" : "text-text-secondary hover:text-text-primary"
                  }`}
              >
                OUR JOURNEY
              </button>
              <button
                onClick={() => setActiveTab("values")}
                className={`font-title font-bold text-xs tracking-widest uppercase transition-all ${activeTab === "values" ? "text-brand-orange" : "text-text-secondary hover:text-text-primary"
                  }`}
              >
                OUR VALUES
              </button>
            </div>

            {activeTab === "story" ? (
              <div className="flex flex-col gap-4">
                <h2 className="text-2xl font-title font-bold text-text-primary">From Local Grid to Global System</h2>
                <p className="text-text-secondary text-xs leading-relaxed">
                  Supply Spot was started in Coimbatore and Bangalore with one clear vision: the core enterprise stack is due for a paradigm shift. Traditional ERP systems operate as static databases. We set out to turn these static layers into live, autonomous pipelines.
                </p>
                <p className="text-text-secondary text-xs leading-relaxed">
                  Today, we sync across international borders, enabling automatic onboarding audits, live dock slots scheduling, and instant matching releases with zero human interaction required for standard paths.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                <h2 className="text-2xl font-title font-bold text-text-primary">Architecting with Clean Integrity</h2>
                <div className="flex flex-col gap-4">
                  {coreValues.map((val, idx) => (
                    <div key={idx} className="flex gap-4 p-4 rounded-xl bg-bg-secondary/40 border border-brand-orange/10">
                      <div className="w-9 h-9 rounded-lg bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center shrink-0">
                        {val.icon}
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-text-primary mb-1">{val.title}</h4>
                        <p className="text-text-secondary text-[11px] leading-relaxed">{val.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ScrollReveal>

          {/* Right Column: Timeline Display */}
          <ScrollReveal delay={0.15} className="lg:col-span-7 w-full">
            <h3 className="text-lg font-title font-bold text-text-primary mb-8 text-left uppercase tracking-wider">Operational Milestones</h3>
            <div className="relative border-l border-brand-orange/20 pl-6 flex flex-col gap-8 text-left">
              {milestones.map((milestone, idx) => (
                <div key={idx} className="relative group">
                  {/* Timeline bullet */}
                  <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-bg-primary border-2 border-brand-orange flex items-center justify-center group-hover:scale-125 transition-transform">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-orange group-hover:bg-brand-amber transition-colors" />
                  </div>
                  <div>
                    <span className="inline-block px-2 py-0.5 rounded bg-brand-orange/10 border border-brand-orange/20 text-[10px] font-bold text-brand-orange mb-2">
                      {milestone.year}
                    </span>
                    <h4 className="text-sm font-bold text-text-primary mb-1 group-hover:text-brand-orange transition-colors">
                      {milestone.title}
                    </h4>
                    <p className="text-text-secondary text-xs leading-relaxed">
                      {milestone.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* LEADERSHIP SECTION */}
      <section className="px-6 py-24 max-w-7xl mx-auto relative z-10 border-t border-border-theme text-center">
        <ScrollReveal className="max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 text-brand-amber text-xs font-bold uppercase tracking-wider mb-6">
            <Users className="w-3.5 h-3.5" />
            <span>OPERATIONAL LEADERSHIP</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 text-text-primary font-title">
            The Leadership Team
          </h2>
          <p className="text-text-secondary text-sm">
            Meet the systems architects, logistics directors, and software developers guiding the platform.
          </p>
        </ScrollReveal>

        <ScrollReveal staggerChildren={0.06} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, index) => (
            <ScrollRevealItem key={index} className="flex">
              <div
                className="glass-panel p-6 rounded-2xl border border-brand-orange/10 hover:border-brand-orange/30 hover:scale-[1.02] transition-all duration-300 flex flex-col text-left relative group overflow-hidden w-full"
              >
                {/* Profile image proxy container */}
                <div className="w-full h-44 rounded-xl bg-gradient-to-br from-bg-secondary to-bg-primary border border-text-muted/10 flex items-center justify-center relative overflow-hidden mb-6 group-hover:border-brand-orange/30 transition-all">
                  {/* Visual glow background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${member.color} opacity-[0.03] group-hover:opacity-[0.08] transition-all duration-300`} />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center flex flex-col items-center">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-tr ${member.color} p-0.5 shadow-lg shadow-black/25 flex items-center justify-center text-white text-xl font-black mb-2 select-none group-hover:scale-105 transition-all duration-500`}>
                      {member.initials}
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-text-primary mb-1 group-hover:text-brand-orange transition-colors">
                      {member.name}
                    </h3>
                    <div className="text-[10px] font-bold text-brand-amber tracking-wider uppercase mb-3">
                      {member.role}
                    </div>
                    <p className="text-text-secondary text-xs leading-relaxed mb-4">
                      {member.bio}
                    </p>
                  </div>
                </div>
              </div>
            </ScrollRevealItem>
          ))}
        </ScrollReveal>
      </section>

      {/* CTA SECTION */}
      <section className="px-6 py-20 max-w-5xl mx-auto relative z-10">
        <ScrollReveal className="glass-panel p-10 md:p-12 rounded-3xl border border-brand-orange/15 bg-gradient-to-r from-bg-secondary/40 to-bg-primary relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8 text-left">
          <div className="max-w-xl">
            <h2 className="text-xl md:text-2xl font-title font-bold mb-3">Ready to optimize your network?</h2>
            <p className="text-text-secondary text-xs md:text-sm leading-relaxed">
              Review our features in detail or configure your operational rules inside a custom staging sandbox with one of our systems engineers.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              className="px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 whitespace-nowrap"
            >
              TALK TO AN ENGINEER
            </Link>
          </div>
        </ScrollReveal>
      </section>
      <Footer />
    </div>
  );
}
